import { useState, Dispatch, SetStateAction, useEffect } from "react";
import axios from "axios";
import getCookie from "./Cookie";

export const deepEqual = (x: any, y: any): boolean => {
  if (x === y) {
    return true;
  } else if (Array.isArray(x) && Array.isArray(y)) {
    if (x.length != y.length) {
      return false;
    }
    for (var i = 0; i < x.length; i++) {
      if (!deepEqual(x[i], y[i])) {
        return false;
      }
    }
    return true;
  } else if (isObject(x) && isObject(y)) {
    if (Object.keys(x).length != Object.keys(y).length) {
      return false;
    }
    for (var prop in x) {
      if (y.hasOwnProperty(prop)) {
        if (!deepEqual(x[prop], y[prop])) return false;
      } else {
        return false;
      }
    }

    return true;
  } else {
    return false;
  }
};

export const isObject = (object: any): boolean => {
  return object != null && typeof object === "object";
};

const saveToSessionStorage = (key: string, value: any) => {
  try {
    const data = JSON.stringify(value);
    const dataSize = new Blob([data]).size;
    if (dataSize < 5000000) {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch (e: unknown) {
    if (e instanceof Error) {
      console.error(`Error setting item with key "${key}":`, e.message);
    } else {
      console.error(`Unknown error setting item with key "${key}":`, e);
    }
  }
};

export function usePersistedState<T>(
  storeKey: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    const saved = sessionStorage.getItem(storeKey);
    return saved !== null ? (JSON.parse(saved) as T) : defaultValue;
  });

  useEffect(() => {
    saveToSessionStorage(storeKey, state);
  }, [state]);

  return [state, setState];
}

// Utility function to get the key based on URL query parameters
const getLocalStorageKey = (stateName: string) => {
  const queryParams = new URLSearchParams(window.location.search);
  return `${stateName}-${queryParams.toString()}`; // e.g., "treatmentOptions-param1=value1&param2=value2"
};

export const useQueryPersistedState = <T,>(
  stateName: string,
  defaultValue: T
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [state, setState] = useState<T>(() => {
    const key = getLocalStorageKey(stateName);
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  });

  useEffect(() => {
    const key = getLocalStorageKey(stateName);
    localStorage.setItem(key, JSON.stringify(state));
  }, [state]);

  return [state, setState];
};

export const getAxisFiltersFromSelectedItems = (
  selectedItems: SelectedItems
) => {
  let allFilters: FilterState = {};
  let keyIndex = 0;
  for (let axes in selectedItems) {
    for (let index in selectedItems[axes]) {
      let item = selectedItems[axes][index];
      if (item.filterItems) {
        for (let filter of item.filterItems) {
          console.log(item.filterItems);
          const splitValue = filter.value.split("|");
          console.log("split value", splitValue);
          const versionType = splitValue[0];
          const column = splitValue[1];
          allFilters[keyIndex] = {
            viewable: filter.viewable,
            filterBehavior: "include",
            type: "categorical",
            value: splitValue[2]
              .split(",")
              .map((value: string) => ({ label: value, value: value })),
            versionType,
            column,
          };
          keyIndex += 1;
        }
      }
    }
  }
  let axisFilterValues: FilterState = allFilters;
  // let vieweableAxisFilters: Set<string> = new Set()

  // for (let filter of allFilters) {
  //   let splitFilter = filter.split('|')
  //   let combinedFilter = splitFilter[0] + '|' + splitFilter[1] + '|' + splitFilter[2] + '|' + splitFilter[3]
  //   let index = parseInt(splitFilter[1])
  //   let viewable =

  //   let values = splitFilter[4].split(',').map((value: string) => ({label: value, value: value}))

  //   if (!(combinedFilter in _axisFiltersState))
  //     axisFilterValues[index].value = values
  //     // axisFilterValues[splitFilter[0]] =
  //     axisFilterValues[index].viewable = splitFilter[3]
  //     axisFilterValues[splitFilter[1]]
  //     axisFilterValues[splitFilter[1]]
  //   else {
  //     axisFilterValues[combinedFilter] = _axisFiltersState[combinedFilter]
  //   }
  // }
  return axisFilterValues;
};

export function getIndexMapOfStrArray(arr: string[]): Record<string, number> {
  return arr.reduce(
    (acc: Record<string, number>, item: string, index, number) => {
      acc[item] = index;
      return acc;
    },
    {}
  );
}

type Trace = {
  x?: (number | number[])[];
  y?: (number | number[])[];
  z?: (number | number[][])[];
};

type MinMax = {
  min: number | null;
  max: number | null;
};

type Result = {
  x: MinMax;
  y: MinMax;
  z: MinMax;
};

export function getPlotLayersMinMax(traces: Trace[]): Result {
  const result: Result = {
    x: { min: Infinity, max: -Infinity },
    y: { min: Infinity, max: -Infinity },
    z: { min: Infinity, max: -Infinity },
  };

  const flatten = (arr: unknown): number[] => {
    return (arr as unknown[])
      .flat(Infinity)
      .filter((v): v is number => typeof v === "number" && !isNaN(v));
  };

  traces.forEach((trace) => {
    if (Array.isArray(trace.x)) {
      const xVals = flatten(trace.x);
      xVals.forEach((val) => {
        result.x.min = Math.min(result.x.min!, val);
        result.x.max = Math.max(result.x.max!, val);
      });
    }

    if (Array.isArray(trace.y)) {
      const yVals = flatten(trace.y);
      yVals.forEach((val) => {
        result.y.min = Math.min(result.y.min!, val);
        result.y.max = Math.max(result.y.max!, val);
      });
    }

    if (Array.isArray(trace.z)) {
      const zVals = flatten(trace.z);
      zVals.forEach((val) => {
        result.z.min = Math.min(result.z.min!, val);
        result.z.max = Math.max(result.z.max!, val);
      });
    }
  });

  (["x", "y", "z"] as const).forEach((axis) => {
    if (result[axis].min === Infinity) result[axis].min = null;
    if (result[axis].max === -Infinity) result[axis].max = null;
  });

  return result;
}

type Matrix = number[][];

/** Compute Euclidean distance between two vectors */
function euclideanDistance(a: number[], b: number[]): number {
    let sum = 0;
    for (let i = 0; i < a.length; i++) {
        const d = a[i] - b[i];
        sum += d * d;
    }
    return Math.sqrt(sum);
}

/** Compute full pairwise distance matrix */
function computeDistanceMatrix(data: Matrix): Matrix {
    const n = data.length;
    const distMatrix: Matrix = Array.from({ length: n }, () => Array(n).fill(0));
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const d = euclideanDistance(data[i], data[j]);
            distMatrix[i][j] = d;
            distMatrix[j][i] = d;
        }
    }
    return distMatrix;
}

/** Hierarchical clustering using single linkage — returns leaf order */
export function clusterOrder(data: Matrix): number[] {
    const n = data.length;
    const distMatrix = computeDistanceMatrix(data);

    // Start with each point as its own cluster
    const clusters: number[][] = Array.from({ length: n }, (_, i) => [i]);

    while (clusters.length > 1) {
        let minDist = Infinity;
        let pair: [number, number] = [0, 1];

        // Find the pair of clusters with the smallest minimum distance
        for (let i = 0; i < clusters.length; i++) {
            for (let j = i + 1; j < clusters.length; j++) {
                let d = Infinity;
                for (const a of clusters[i]) {
                    for (const b of clusters[j]) {
                        d = Math.min(d, distMatrix[a][b]);
                    }
                }
                if (d < minDist) {
                    minDist = d;
                    pair = [i, j];
                }
            }
        }

        // Merge the closest pair
        const [i, j] = pair;
        clusters[i] = clusters[i].concat(clusters[j]);
        clusters.splice(j, 1);
    }

    // Return the final ordered list of indices
    return clusters[0];
}

export async function clusterAPI(data: Matrix): Promise<{rowOrder:number[], colOrder:number[]}> {
  try {
    const response = await axios.post('/tumors/api/v1/cluster', data, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCookie('csrftoken'),
      },
    });
    console.log("Clustering response:", response.data);
    return {rowOrder: response.data.row_order, colOrder: response.data.col_order};
  } catch (err: any) {
    if (err.response && err.response.status === 403) {
      window.location.href =
        '/accounts/login/?next=' +
        encodeURIComponent(window.location.pathname + window.location.search);
    } else {
      console.error(err);
    }
    // Fallback: return list of ordered indices
    const fallback = Array.from({ length: data.length }, (_, i) => i)
    return {rowOrder: fallback, colOrder: fallback};
  }
}

export function checkForNulls(arr2D: (number | null)[][]): boolean {
  for (let i = 0; i < arr2D.length; i++) {
    const row = arr2D[i];
    for (let j = 0; j < row.length; j++) {
      if (row[j] === null) {
        return (true);
      }
    }
  }
  return false;
}


export const getFilterKeys = ( fs: FilterState ): FilterKeys[] => {
  return Object.keys(fs).map(k => parseInt(k)) as FilterKeys[];
}